import {useCallback} from 'react';
import {Form} from 'formik';
import {useTranslation} from 'react-i18next';
import ActionButtons from '../../common/layout/ActionButtons';
import EnsurePermission from '../../common/EnsurePermission';
import {Button, ButtonWithLoader} from '../../common/Button';
import permissionsInfo from '../../../constants/permissionsInfo';
import {AddSurveyFormProps} from '../types';
import useOnUnsavedChanges from '../../../hooks/useOnUnsavedChanges';
import {AutocompleteField} from '../../common/formik/fields';
import {FormGroup} from '@mui/material';

enum Field {
  PATIENT_ID = 'patientId',
  CALL_SHEET_ID = 'callSheetId',
  PATIENT_NAME = 'patientName'
}

const AddSurveyForm = ({
  patientsOptions,
  patientsCallSheets,
  formikProps: {
    setFieldValue,
    setFieldTouched,
    values,
    isSubmitting,
    dirty
  },
  backToSurveyList
}: AddSurveyFormProps) => {
  const {t} = useTranslation(['surveys', 'common']);
  useOnUnsavedChanges(dirty);

  const hasValues = !!values?.[Field.PATIENT_ID] &&
    !!values?.[Field.CALL_SHEET_ID];

  const handleChangePatient = useCallback((value) => {
    if (values?.[Field.CALL_SHEET_ID]) {
      setFieldValue(Field.CALL_SHEET_ID, '');
      setFieldTouched(Field.CALL_SHEET_ID, false);
    }
    const patientName =
    patientsOptions.find((patient) => patient.value === value)?.fullName || '';
    setFieldValue(Field.PATIENT_NAME, patientName);
    setFieldValue(Field.PATIENT_ID, value || '');
    setFieldTouched(Field.PATIENT_ID, true);
  }, [values?.[Field.CALL_SHEET_ID]]);

  const callSheetOptions = patientsCallSheets[values?.[Field.PATIENT_ID]] || [];

  return (
    <Form className="form">
      <div className="creation-form-wrapper">
        <FormGroup>
          <AutocompleteField
            name={Field.PATIENT_ID}
            label={t('newSurveyForm.fields.patient')}
            options={patientsOptions}
            required
            disabled={isSubmitting}
            onChangeCallback={handleChangePatient}
          />
        </FormGroup>

        <FormGroup>
          <AutocompleteField
            name={Field.CALL_SHEET_ID}
            label={t('newSurveyForm.fields.callSheet')}
            options={callSheetOptions}
            required
            disabled={isSubmitting}
          />
        </FormGroup>
      </div>

      <ActionButtons>
        <EnsurePermission
          elementPermissions={permissionsInfo.surveys.create}
        >
          <ButtonWithLoader
            type="submit"
            testId="create-new-survey"
            label={t('newSurveyForm.submitBtn')}
            disabled={!hasValues}
            isSubmitting={isSubmitting}
          />
        </EnsurePermission>

        <Button
          label={t('common:cancel')}
          variant="outlined"
          color="secondary"
          onClick={backToSurveyList}
        />
      </ActionButtons>
    </Form>
  );
};

export default AddSurveyForm;
